import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlternativeModeSchema,
  ApiClientError,
  DecorKindSchema,
  type AlternativeMode,
  type ApiClientErrorCode,
  type DecorKind,
  type DecorListItem,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { DecorResultCard } from './DecorResultCard';
import { useDecorImageUrls } from './useDecorImageUrls';
import {
  decorFilterToQuery,
  readDecorFilterParams,
  toAlternativesFilter,
} from './decorFilterParams';

/**
 * Container Экрана 2.2.8 (Альтернативы декора). См. ADR-0005.
 * Простой single-component (не split на Presenter — экран небольшой, без
 * сложных state-комбинаций; ADR-0005 §«Откат» это разрешает для тривиальных
 * экранов).
 *
 * URL: `/decors/:kind/:globalId/alternatives?mode=lighter|darker|warmer|cooler`.
 * Container читает kind/globalId/mode → вызывает apiClient.decors.alternatives →
 * отображает 4 карточки через `DecorResultCard` (тот же компонент Phase 6).
 */

const MODE_LABELS: Record<AlternativeMode, string> = {
  lighter: 'Светлее',
  darker: 'Темнее',
  warmer: 'Теплее',
  cooler: 'Холоднее',
};

export function AlternativesScreen() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string; globalId: string }>();
  const [searchParams] = useSearchParams();

  const kindParse = DecorKindSchema.safeParse(params.kind);
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';
  // react-router-dom v7 уже декодирует path params; повторный
  // decodeURIComponent ломается на Global_ID с `%` (URIError).
  const globalId = params.globalId ?? '';
  const modeRaw = searchParams.get('mode');
  const modeParse = AlternativeModeSchema.safeParse(modeRaw);
  const mode: AlternativeMode | null = modeParse.success ? modeParse.data : null;

  // ADR-0055: активный фильтр пришёл в URL (из карточки) → подаём его в alternatives.
  // R3: persistent intent. `filterKey` — стабильный ключ для deps useEffect.
  const filterParams = useMemo(() => readDecorFilterParams(searchParams), [searchParams]);
  const filterKey = decorFilterToQuery(filterParams);

  const [items, setItems] = useState<readonly DecorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!mode || !globalId) {
      setError({ code: 'INVALID_INPUT', message: 'Не указан режим альтернатив' });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(undefined);
    let cancelled = false;
    apiClient.decors.alternatives(kind, globalId, mode, toAlternativesFilter(filterParams)).then(
      (res) => {
        if (cancelled) return;
        setItems(res.items);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kind, globalId, mode, filterParams]);

  // Фото-2 (PR-DATA4): signed-URL для фото альтернатив.
  const photoKeys = useMemo(() => items.map((i) => i.primaryPhotoKey), [items]);
  const { urls: imageUrls } = useDecorImageUrls(photoKeys);

  const title = mode ? `${MODE_LABELS[mode]} похожие декоры` : 'Альтернативы';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={title} />
      <section className="flex-1 p-4 overflow-y-auto">
        {loading && <p className="text-muted text-[18px] text-center mt-12 m-0">Загружаем…</p>}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
            <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
              Не удалось получить альтернативы.
            </p>
            {error.message && <p className="text-muted text-sm">{error.message}</p>}
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-muted text-[18px] text-center mt-12 m-0">Альтернативы не найдены.</p>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="flex flex-col gap-5">
            {items.map((item) => (
              <DecorResultCard
                key={item.globalId}
                item={item}
                dominantLab={item.dominantLab}
                imageUrl={item.primaryPhotoKey ? (imageUrls[item.primaryPhotoKey] ?? null) : null}
                onItemClick={(id) =>
                  // Сохраняем активный фильтр в цепочке альтернатива → карточка (ADR-0055).
                  navigate(
                    `/decors/${encodeURIComponent(kind)}/${encodeURIComponent(id)}${filterKey ? `?${filterKey}` : ''}`,
                  )
                }
              />
            ))}
          </div>
        )}
      </section>
      <BottomBar />
    </main>
  );
}
